import { type CountryCode, parsePhoneNumber } from 'libphonenumber-js/mobile';

export type ComparisonResult = 'SAME' | 'DIFFERENT';
export interface PhoneNumberComparison {
  result: ComparisonResult;
  parts: { prefix: string; phoneNumber: string; regionCode: string };
}

export class PhoneNumber {
  readonly prefix: string;
  readonly phoneNumber: string;
  readonly regionCode: string;

  constructor(props: { prefix: string; phoneNumber: string; regionCode: string }) {
    this.prefix = props.prefix;
    this.phoneNumber = props.phoneNumber;
    this.regionCode = props.regionCode;
  }

  equals(other: PhoneNumber): boolean {
    return (
      this.prefix === other.prefix &&
      this.phoneNumber === other.phoneNumber &&
      this.regionCode.toLowerCase() === other.regionCode.toLowerCase()
    );
  }

  toString(): string {
    return `${this.prefix}${this.phoneNumber}`;
  }

  static compare({
    phoneNumberA,
    phoneNumberB,
  }: {
    phoneNumberB: PhoneNumber;
    phoneNumberA: PhoneNumber;
  }): PhoneNumberComparison {
    if (phoneNumberB.equals(phoneNumberA)) {
      return {
        result: 'SAME',
        parts: {
          prefix: 'SAME',
          phoneNumber: 'SAME',
          regionCode: 'SAME',
        },
      };
    } else {
      return {
        result: 'DIFFERENT',
        parts: {
          prefix: phoneNumberB.prefix === phoneNumberA.prefix ? 'SAME' : 'DIFFERENT',
          phoneNumber: phoneNumberB.phoneNumber === phoneNumberA.phoneNumber ? 'SAME' : 'DIFFERENT',
          regionCode:
            phoneNumberB.regionCode.toLowerCase() === phoneNumberA.regionCode.toLowerCase()
              ? 'SAME'
              : 'DIFFERENT',
        },
      };
    }
  }

  static create(props: { prefix: string; phoneNumber: string; regionCode: string }) {
    if (!props.prefix || !props.phoneNumber || !props.regionCode) {
      throw new Error('Missing required properties to create PhoneNumber');
    }
    return new PhoneNumber(props);
  }

  static parse({
    phoneNumber,
    allowedDialCodes,
    defaultRegionCode,
  }: {
    phoneNumber: string;
    allowedDialCodes: Array<{ dialCode: string; code: string }>;
    defaultRegionCode: string;
  }) {
    try {
      const parsedPhoneNumber = parsePhoneNumber(
        `${phoneNumber}`,
        defaultRegionCode as CountryCode
      );
      if (!parsedPhoneNumber.isValid()) {
        if (parsedPhoneNumber.country !== 'AR') return undefined;
        // workaround for Argentinian phone numbers
        // https://github.com/google/libphonenumber/blob/master/FAQ.md#why-is-this-number-from-argentina-ar-or-mexico-mx-not-identified-as-the-right-number-type
        const reparsedPhoneNumber = parsePhoneNumber(
          `9${parsedPhoneNumber.nationalNumber}`,
          parsedPhoneNumber.country
        );
        if (!reparsedPhoneNumber.isValid()) return undefined;
      }

      if (parsedPhoneNumber && parsedPhoneNumber.countryCallingCode) {
        const dialCode = `+${parsedPhoneNumber.countryCallingCode}`;
        const matchingCountry = allowedDialCodes.find((country) => country.dialCode === dialCode);

        if (matchingCountry) {
          return PhoneNumber.create({
            prefix: matchingCountry.dialCode,
            phoneNumber: parsedPhoneNumber.nationalNumber,
            regionCode: matchingCountry.code,
          });
        }
      }
    } catch (error) {
      //no-op, we will return undefined
    }

    return undefined;
  }
}
